// Client-side diff between two entity snapshots, used by the reactivate modal
// preview. Mirrors the backend versioning diff but categorized for display.

const num = (v) => (v === null || v === undefined ? null : Number(v));

function coordsChanged(a, b) {
  return num(a?.lat) !== num(b?.lat) || num(a?.lng) !== num(b?.lng);
}

function itemCount(entityType, snapshot) {
  if (!snapshot) return 0;
  if (entityType === 'route') return (snapshot.stops || []).length;
  return 1;
}

// `current` = the live/current snapshot, `target` = the version being reactivated.
// "added" means items the target reintroduces that the current state lacks.
export function buildReactivationDiff(entityType, current, target) {
  const result = {
    currentCount: itemCount(entityType, current),
    afterCount: itemCount(entityType, target),
    metaChanges: [],
    itemsAdded: [],
    itemsRemoved: [],
    itemsReordered: []
  };

  if (entityType === 'route') {
    const meta = [
      ['routeName', 'Route name'],
      ['routeNumber', 'Route number'],
      ['direction', 'Direction']
    ];
    meta.forEach(([key, label]) => {
      if ((current?.[key] ?? '') !== (target?.[key] ?? '')) {
        result.metaChanges.push({
          field: label,
          from: current?.[key] ?? '—',
          to: target?.[key] ?? '—'
        });
      }
    });

    const currentStops = new Map((current?.stops || []).map((s) => [String(s.stopId), s]));
    const targetStops = new Map((target?.stops || []).map((s) => [String(s.stopId), s]));

    targetStops.forEach((stop, id) => {
      if (!currentStops.has(id)) {
        result.itemsAdded.push({ id, name: stop.stopName, sequence: stop.sequence });
      }
    });
    currentStops.forEach((stop, id) => {
      if (!targetStops.has(id)) {
        result.itemsRemoved.push({ id, name: stop.stopName, sequence: stop.sequence });
      }
    });
    targetStops.forEach((stop, id) => {
      const before = currentStops.get(id);
      if (!before) return;
      if (num(before.sequence) !== num(stop.sequence)) {
        result.itemsReordered.push({
          id,
          name: stop.stopName,
          from: before.sequence,
          to: stop.sequence
        });
      } else if (coordsChanged(before, stop)) {
        result.itemsReordered.push({ id, name: stop.stopName, moved: true });
      }
    });
  } else {
    const fields = [
      ['stopId', 'Stop ID'],
      ['stopName', 'Name']
    ];
    fields.forEach(([key, label]) => {
      if ((current?.[key] ?? '') !== (target?.[key] ?? '')) {
        result.metaChanges.push({
          field: label,
          from: current?.[key] ?? '—',
          to: target?.[key] ?? '—'
        });
      }
    });
    if (coordsChanged(current, target)) {
      result.metaChanges.push({
        field: 'Coordinates',
        from: current ? `${current.lat}, ${current.lng}` : '—',
        to: target ? `${target.lat}, ${target.lng}` : '—'
      });
    }
  }

  result.hasChanges =
    result.metaChanges.length > 0 ||
    result.itemsAdded.length > 0 ||
    result.itemsRemoved.length > 0 ||
    result.itemsReordered.length > 0;

  return result;
}
